// ECharts helpers — pure functions that build option objects and parse rows.

import type { EChartsOption } from "echarts";
import type { DataRow, FieldRef, VisualData, VisualSpec } from "../types/pbi";
// AGENT-C: only need the palette here; PBI text/axis styles now live in
// constants below so axis defaults can be exported and re-used directly.
import { palette } from "../theme";
import type { FilterEntry, FilterKey } from "../state/filterStore";

/** Pull the first column/measure name out of a fields bucket. */
export function bucketKey(refs: FieldRef[] | undefined): string | undefined {
  if (!refs || refs.length === 0) return undefined;
  const r = refs[0];
  return r.displayName ?? r.column ?? r.measure ?? r.queryRef ?? undefined;
}

/** Pull all keys out of a bucket (for multi-series charts). */
export function bucketKeys(refs: FieldRef[] | undefined): string[] {
  if (!refs) return [];
  return refs.map(
    (r) => r.displayName ?? r.column ?? r.measure ?? r.queryRef ?? "",
  ).filter(Boolean);
}

/** Pull the FieldRef itself (used for emitting filters). */
export function firstFieldRef(refs: FieldRef[] | undefined): FieldRef | undefined {
  return refs && refs.length > 0 ? refs[0] : undefined;
}

/** Empty-detector used by chart wrappers. */
export function isEmpty(data: VisualData): boolean {
  if (data.status === "empty") return true;
  if (Array.isArray(data.rows)) return data.rows.length === 0;
  if (Array.isArray(data.options)) return data.options.length === 0;
  if (data.value !== undefined) return data.value === null;
  return true;
}

// AGENT-C: PBI-style chart defaults.
// These constants are exported so individual chart wrappers can re-use the
// same axis / gridline / tooltip styling when they layer their own config on
// top of `baseChartOption()`.
//
// Per the PBI theme rule we use *dotted* gridlines (#e0dedd), a hairline axis
// line in the same color, and 11px / #605e5c labels in Segoe UI.
export const PBI_FONT_FAMILY = "'Segoe UI', 'Roboto', sans-serif";
export const PBI_TEXT_COLOR = "#252423";
export const PBI_AXIS_LABEL_COLOR = "#605e5c";
export const PBI_AXIS_LINE_COLOR = "#e0dedd";
export const PBI_GRID_LINE_COLOR = "#e0dedd";

// AGENT-C: shared axis defaults — both xAxis and yAxis pick these up so every
// chart gets dotted gridlines + Segoe UI labels for free.
export const pbiAxisDefault = {
  axisLine: {
    show: true,
    lineStyle: { color: PBI_AXIS_LINE_COLOR, width: 1 },
  },
  axisTick: { lineStyle: { color: PBI_AXIS_LINE_COLOR } },
  axisLabel: {
    color: PBI_AXIS_LABEL_COLOR,
    fontFamily: PBI_FONT_FAMILY,
    fontSize: 11,
  },
  splitLine: {
    show: true,
    lineStyle: { type: "dotted" as const, color: PBI_GRID_LINE_COLOR },
  },
};

/** Shared base options applied to every ECharts chart. */
export function baseChartOption(): Partial<EChartsOption> {
  return {
    // AGENT-C: PBI default text style — Segoe UI fallback chain, primary text color.
    textStyle: {
      fontFamily: PBI_FONT_FAMILY,
      fontSize: 12,
      color: PBI_TEXT_COLOR,
    },
    color: [...palette],
    grid: { left: 40, right: 16, top: 24, bottom: 28, containLabel: true },
    // AGENT-C: dotted gridlines + 11px muted labels applied to both axes.
    xAxis: { ...pbiAxisDefault },
    yAxis: { ...pbiAxisDefault },
    tooltip: {
      trigger: "axis",
      // AGENT-C: PBI tooltip — white surface, hairline border, Segoe UI 12.
      backgroundColor: "#ffffff",
      borderColor: PBI_AXIS_LINE_COLOR,
      borderWidth: 1,
      textStyle: {
        fontFamily: PBI_FONT_FAMILY,
        fontSize: 12,
        color: PBI_TEXT_COLOR,
      },
    },
    legend: {
      type: "scroll",
      bottom: 0,
      // AGENT-C: PBI legend — 11px muted labels.
      textStyle: {
        fontFamily: PBI_FONT_FAMILY,
        fontSize: 11,
        color: PBI_AXIS_LABEL_COLOR,
      },
      icon: "roundRect",
      itemWidth: 10,
      itemHeight: 10,
    },
    animation: false,
  };
}


/** Build a click-handler for category-axis charts. */
export interface ClickContext {
  visualId: string;
  spec: VisualSpec;
  onFilter: (key: FilterKey, entry: FilterEntry) => void;
}

export function makeCategoryClick(
  ctx: ClickContext,
  field: FieldRef | undefined,
): (params: { name?: string; value?: unknown }) => void {
  return (params) => {
    if (!field?.table || !field?.column) return;
    const value = params.name ?? null;
    if (value === null) return;
    ctx.onFilter(`${field.table}.${field.column}`, {
      op: "eq",
      value: value as string,
      source: ctx.visualId,
    });
  };
}

/** Read a single column out of long-form rows. */
export function pickColumn(
  rows: DataRow[],
  key: string,
): Array<string | number | boolean | null> {
  return rows.map((r) => r[key] ?? null);
}

// P1-selection: PBI-style "dim the unselected" highlight helper.
//
// When a filter is active on the same category that drives a chart, the
// matching segments stay opaque (1.0) and everything else dims to 0.4.
// No-op when no filter matches the chart's category.
//
// Tolerant to key variants:
//   - filter key   may be "Table.Column" or just "Column"
//   - categoryKey  may be "Table.Column", "Column", or a displayName
// We match on the trailing column segment after the last ".".
function tail(key: string): string {
  const ix = key.lastIndexOf(".");
  return ix >= 0 ? key.slice(ix + 1) : key;
}

function filterValueSet(entry: FilterEntry): Set<string> {
  const out = new Set<string>();
  const v = entry.value;
  if (v === null || v === undefined) return out;
  if (Array.isArray(v)) {
    for (const x of v) out.add(String(x));
  } else {
    out.add(String(v));
  }
  return out;
}

function findMatchingFilter(
  filters: Record<string, FilterEntry>,
  categoryKey: string,
): FilterEntry | undefined {
  if (!categoryKey) return undefined;
  const targetTail = tail(categoryKey).toLowerCase();
  // Exact-match first (full "Table.Column").
  for (const [k, v] of Object.entries(filters)) {
    if (k === categoryKey) return v;
  }
  // Trailing-segment match (covers "Table.Column" filter vs "Column" categoryKey
  // and vice-versa).
  for (const [k, v] of Object.entries(filters)) {
    if (tail(k).toLowerCase() === targetTail) return v;
  }
  return undefined;
}

// P1-selection: keep selected items at 1.0, dim the rest to 0.4.
export function applySelectionStyle(
  option: any,
  filters: Record<string, FilterEntry>,
  categoryKey: string | undefined,
  categories: Array<string | number | boolean | null | undefined>,
): any {
  if (!option || !filters || Object.keys(filters).length === 0) return option;
  if (!categoryKey) return option;
  const entry = findMatchingFilter(filters, categoryKey);
  if (!entry) return option;

  // Negative ops shouldn't drive a highlight (they exclude rather than select).
  if (entry.op === "neq" || entry.op === "not_in") return option;

  const selected = filterValueSet(entry);
  if (selected.size === 0) return option;

  // Build the mask once per category index.
  const mask: boolean[] = categories.map((c) => {
    if (c === null || c === undefined) return false;
    return selected.has(String(c));
  });
  // If nothing matches at all, keep chart unchanged (avoids dimming everything
  // when the filter targets a different category space).
  if (!mask.some(Boolean)) return option;

  // Deep-clone to avoid mutating caller-owned option objects.
  const next = JSON.parse(JSON.stringify(option));
  const series = Array.isArray(next.series) ? next.series : [];
  next.series = series.map((s: any) => {
    if (!s || !Array.isArray(s.data)) return s;
    const data = s.data.map((d: any, i: number) => {
      const isSelected = mask[i] ?? false;
      const opacity = isSelected ? 1.0 : 0.4;
      if (d === null || d === undefined || typeof d === "number" || typeof d === "string") {
        return { value: d, itemStyle: { opacity } };
      }
      // Object form — preserve existing fields, layer opacity onto itemStyle.
      return {
        ...d,
        itemStyle: { ...(d.itemStyle ?? {}), opacity },
      };
    });
    return { ...s, data };
  });
  return next;
}
